import { useEffect, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import type { ChatMessage, HypeMeterSnapshot, StreamEvent } from '@hypevault/shared-types';
import { WidgetView } from './widgets/WidgetView';
import { GemLeaderboardWidget } from './GemLeaderboardWidget';
import { HypePetsLeaderboardWidget } from './HypePetsLeaderboardWidget';
import { ChaosScrambleWidget } from './widgets/ChaosScrambleWidget';
import { EmojiDecoderWidget } from './widgets/EmojiDecoderWidget';
import { HypeMillionWidget } from './widgets/HypeMillionWidget';
import { HypeDealWidget } from './widgets/HypeDealWidget';
import { DrawGuessWidget } from './widgets/DrawGuessWidget';
import { MagicPoolBallWidget, type MagicPoolBallEvent } from './widgets/MagicPoolBall';
import { SlotsWidget, type SlotsEvent } from './widgets/Slots';
import { HypefishWidget } from './widgets/HypefishWidget';
import { HypefishFullscreenWidget } from './widgets/HypefishFullscreenWidget';
import { HypeEggWidget } from './widgets/HypeEggWidget';
import { HypeEggPanelWidget } from './widgets/HypeEggPanelWidget';
import { HypePetsFullscreenWidget } from './widgets/HypePetsFullscreenWidget';
import { SpotifyNowPlayingWidget } from './widgets/SpotifyNowPlayingWidget';
import { JumpscareWidget, type JumpscarePayload } from './widgets/JumpscareWidget';
import { UnifiedGameWidget, type ActiveGameType } from './widgets/UnifiedGameWidget';
import { HypeMeterWidget } from './widgets/HypeMeterWidget';
import { ImageRotatorWidget, type ImageRotatorControlEvent } from './widgets/ImageRotatorWidget';
import { CardTimerWidget, type CardTimerEvent } from './widgets/CardTimerWidget';
import type { RoundSnapshot } from '@hypevault/chaos-scramble';
import type { RoundSnapshot as EmojiSnapshot } from '@hypevault/emoji-decoder';
import type { GameSnapshot as HypeMillionSnapshot } from '@hypevault/hype-million';
import type { GameSnapshot as HypeDealSnapshot } from '@hypevault/hype-deal';
import type { RoundSnapshot as DrawGuessSnapshot } from '@hypevault/draw-guess';

interface WidgetConfig {
  streamerId: string;
  widgetType: string;
  fields: Record<string, unknown>;
  isEnabled: boolean;
  streamerTier: 'free' | 'paid';
}

interface MarathonReveal {
  id: string;
  eventType: string;
  contributorName: string;
  secondsAdded: number;
  createdAt: string;
}

const WS_URL = import.meta.env.VITE_WS_URL ?? 'http://localhost:3002';

export function WidgetApp({ widgetType }: { widgetType: string }) {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recentEvent, setRecentEvent] = useState<StreamEvent | null>(null);
  const [chatMessage, setChatMessage] = useState<ChatMessage | null>(null);
  const [gameStateEvent, setGameStateEvent] = useState<{
    gameType: string;
    state: unknown;
    config: unknown;
    isActive: boolean;
  } | null>(null);
  const [chaosSnapshot, setChaosSnapshot] = useState<RoundSnapshot | null>(null);
  const [emojiSnapshot, setEmojiSnapshot] = useState<EmojiSnapshot | null>(null);
  const [hypeMillionSnapshot, setHypeMillionSnapshot] = useState<HypeMillionSnapshot | null>(null);
  const [hypeDealSnapshot, setHypeDealSnapshot] = useState<HypeDealSnapshot | null>(null);
  const [drawGuessSnapshot, setDrawGuessSnapshot] = useState<DrawGuessSnapshot | null>(null);
  const [magicPoolBallEvent, setMagicPoolBallEvent] = useState<MagicPoolBallEvent | null>(null);
  const [slotsEvent, setSlotsEvent] = useState<SlotsEvent | null>(null);
  const [jumpscareEvent, setJumpscareEvent] = useState<JumpscarePayload | null>(null);
  const [activeGame, setActiveGame] = useState<ActiveGameType | null | undefined>(undefined);
  const [marathonReveal, setMarathonReveal] = useState<MarathonReveal | null>(null);
  const [hypeMeterSnapshot, setHypeMeterSnapshot] = useState<HypeMeterSnapshot | null>(null);
  const [imageRotatorControl, setImageRotatorControl] = useState<ImageRotatorControlEvent | null>(
    null,
  );
  const [cardTimerEvent, setCardTimerEvent] = useState<CardTimerEvent | null>(null);
  const [wsToken, setWsToken] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Missing token');
      return;
    }
    let cancelled = false;
    let hasConfig = false;
    const attemptFetch = async (url: string): Promise<Response> => {
      // 3 attempts at 0s / 2s / 5s before giving up. Transient network
      // blips on the viewer's end shouldn't blank the overlay.
      const delays = [0, 2000, 5000];
      let lastErr: unknown;
      for (const delay of delays) {
        if (cancelled) throw new Error('cancelled');
        if (delay) await new Promise((r) => setTimeout(r, delay));
        try {
          const res = await fetch(url, { cache: 'no-store' });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res;
        } catch (err) {
          lastErr = err;
        }
      }
      throw lastErr ?? new Error('fetch failed');
    };
    const load = async () => {
      try {
        const res = await attemptFetch(`/api/widgets/public?token=${encodeURIComponent(token)}`);
        const json = (await res.json()) as WidgetConfig;
        if (cancelled) return;
        if (json.widgetType !== widgetType) {
          setError(`Token is for "${json.widgetType}", not "${widgetType}"`);
          return;
        }
        hasConfig = true;
        setError(null);
        // Skip setState when the payload is unchanged so widgets don't
        // re-render on every poll just because we got a new object back.
        setConfig((prev) => (prev && JSON.stringify(prev) === JSON.stringify(json) ? prev : json));
      } catch (err) {
        if (cancelled) return;
        // Only surface the red error overlay if we never got a config in
        // the first place. Once a widget is rendering, a transient failure
        // should keep showing the last-known state, not blank to error.
        if (!hasConfig) setError(String(err));
      }
    };
    load();
    // Poll every 15s so dashboard tweaks (variant swap, color overrides,
    // sound URL, enabledEvents) propagate to OBS browser sources without
    // a manual refresh. The endpoint sets Cache-Control: no-store and
    // the JSON-equality check above keeps re-renders to actual changes.
    const interval = setInterval(load, 15_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [token, widgetType]);

  useEffect(() => {
    if (!config?.streamerId || !token) return;
    let cancelled = false;
    const refresh = async () => {
      const delays = [0, 2000, 5000];
      for (const delay of delays) {
        if (cancelled) return;
        if (delay) await new Promise((r) => setTimeout(r, delay));
        try {
          const res = await fetch(`/api/widgets/ws-token?token=${encodeURIComponent(token)}`);
          if (!res.ok) continue;
          const data = await res.json();
          if (!cancelled && data?.token) setWsToken(data.token);
          return;
        } catch {
          // try next backoff
        }
      }
    };
    refresh();
    // Overlay JWTs now expire after 1 hour. Re-mint every 50 minutes so the
    // socket can reconnect with a fresh token if the underlying connection
    // drops. Setting wsToken triggers the socket effect below to reconnect.
    const interval = setInterval(refresh, 50 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [config?.streamerId, token]);

  useEffect(() => {
    if (!wsToken) return;
    const socket: Socket = io(WS_URL, {
      auth: { token: wsToken },
      transports: ['websocket', 'polling'],
    });
    socket.on('event', (event: StreamEvent) => setRecentEvent(event));
    socket.on('chat-message', (msg: ChatMessage) => setChatMessage(msg));
    socket.on(
      'game-state',
      (e: { gameType: string; state: unknown; config: unknown; isActive: boolean }) =>
        setGameStateEvent(e),
    );
    socket.on('chaos-state', (e: RoundSnapshot) => setChaosSnapshot(e));
    socket.on('emoji-state', (e: EmojiSnapshot | { ended: true }) => {
      if (e && 'ended' in e) setEmojiSnapshot(null);
      else setEmojiSnapshot(e);
    });
    socket.on('hype-million-state', (e: HypeMillionSnapshot) => setHypeMillionSnapshot(e));
    socket.on('hype-deal-state', (e: HypeDealSnapshot | null) => setHypeDealSnapshot(e));
    socket.on('draw-guess-state', (e: DrawGuessSnapshot | { ended: true }) => {
      if ('ended' in e) {
        setDrawGuessSnapshot(null);
      } else {
        setDrawGuessSnapshot(e);
      }
    });
    socket.on('magic-pool-ball-trigger', (e: MagicPoolBallEvent) => setMagicPoolBallEvent(e));
    socket.on('slots-trigger', (e: SlotsEvent) => setSlotsEvent(e));
    socket.on(
      'jumpscare',
      (frame: { type?: string; payload?: JumpscarePayload } | JumpscarePayload) => {
        // Envelope tolerance — eventbus publishes `{ type, payload }`, but
        // some upstream callers emit the payload directly. Mirrors the
        // sniff in apps/hype-vison/src/lib/websocket.ts.
        const p =
          frame && 'payload' in frame && frame.payload
            ? frame.payload
            : (frame as JumpscarePayload);
        if (!p?.mediaUrl) return;
        setJumpscareEvent(p);
      },
    );
    socket.on('active-game-changed', (e: { activeGame: ActiveGameType | null }) =>
      setActiveGame(e?.activeGame ?? null),
    );
    socket.on('marathon-reveal', (e: MarathonReveal) => setMarathonReveal(e));
    socket.on('hype-meter-state', (e: HypeMeterSnapshot) => setHypeMeterSnapshot(e));
    socket.on('image-rotator-control', (e: ImageRotatorControlEvent) => setImageRotatorControl(e));
    socket.on('card-timer', (e: CardTimerEvent) => setCardTimerEvent(e));
    return () => {
      socket.disconnect();
    };
  }, [wsToken]);

  if (error) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 12,
          left: 12,
          color: '#f87171',
          fontFamily: 'monospace',
          fontSize: 12,
        }}
      >
        Widget error: {error}
      </div>
    );
  }

  if (!config) return null;
  if (!config.isEnabled) {
    // Surface a clearly-visible "off" diagnostic so streamers don't see a
    // silent black browser source and assume their alerts/widgets are
    // broken. A previous incident had alert-box disabled in the dashboard
    // while the OBS source pointed at /w/alert-box — Twitch webhooks were
    // arriving + Redis was publishing, but the widget short-circuited to
    // `return null` and nothing rendered. Now they see the reason.
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'grid',
          placeItems: 'center',
          fontFamily: "'Poppins', system-ui, sans-serif",
          color: 'white',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(40,16,16,0.92), rgba(60,12,40,0.92))',
            border: '1px solid rgba(248, 113, 113, 0.45)',
            borderRadius: 14,
            padding: '20px 28px',
            maxWidth: 480,
            textAlign: 'center',
            boxShadow: '0 12px 36px rgba(248, 113, 113, 0.22)',
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 3,
              textTransform: 'uppercase',
              color: '#fca5a5',
              marginBottom: 6,
            }}
          >
            Widget Off
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>{widgetType}</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
            This widget is toggled <strong>off</strong> in the dashboard. Flip the switch at{' '}
            <code
              style={{ background: 'rgba(255,255,255,0.08)', padding: '1px 6px', borderRadius: 4 }}
            >
              /dashboard/widgets/{widgetType}
            </code>{' '}
            to start showing events.
          </div>
          <div
            style={{
              fontSize: 10,
              color: 'rgba(255,255,255,0.4)',
              marginTop: 10,
              letterSpacing: 0.4,
            }}
          >
            Hide this browser source in OBS if you don&apos;t want it visible.
          </div>
        </div>
      </div>
    );
  }

  const widget = {
    id: 'standalone',
    widgetType: config.widgetType,
    transform: {
      x: 0,
      y: 0,
      w: 1920,
      h: 1080,
      rotation: 0,
      opacity: 1,
      zIndex: 0,
      locked: false,
      hidden: false,
    },
    fields: config.fields,
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
      }}
    >
      {/*
        Architectural note: these branches handle widgets that don't fit
        WidgetView's prop signature because their state arrives via
        dedicated socket events / control channels rather than the
        generic `latestEvent` + `latestChatMessage` pair.

        Each one needs at least one of:
          - a per-widget Snapshot from its own pub/sub topic (chaos-state,
            emoji-state, hype-million-state, draw-guess-state, wyr-state,
            hype-meter-state, etc.)
          - the browser_source_token (image-rotator fetches /api/image-rotator/public)
          - a trigger event (magic-pool-ball-trigger, slots-trigger)
          - active-game routing context (UnifiedGameWidget)

        Threading all of these through WidgetView's typed props would mean
        an ever-growing surface area on a component that's only used by the
        per-widget URL flow. Special-casing here keeps WidgetView focused on
        the legacy `widget.fields + latestEvent` shape it was built for.

        Watermark policy for these branches (free tier):
          - gem_leaderboard, hypepets_leaderboard, hype_meter:
                                           self-anchored to a screen edge with
            absolute positioning. WidgetView's flex-column watermark wrap
            fights that layout, so skipped here. (Add an internal
            watermark like `game` / `goals` do if branding becomes needed.)
          - image_rotator:                streamer-supplied image content
                                           (same exclusion as static-image).
          - magic_pool_ball, slots:       transient pop-ins (same exclusion
                                           reason as jumpscare / ad-break).
          - chaos_scramble, emoji_decoder, hype_million, draw_guess, game:
                                          render their own watermark inside
            the game shell so it can be anchored to gameplay chrome.

        If a new widget type fits the existing prop shape, prefer adding it
        to WidgetView's switch instead of growing this chain.
      */}
      {config.widgetType === 'gem_leaderboard' ? (
        <GemLeaderboardWidget streamerId={config.streamerId} fields={config.fields} />
      ) : config.widgetType === 'hypepets_leaderboard' ? (
        <HypePetsLeaderboardWidget streamerId={config.streamerId} fields={config.fields} />
      ) : config.widgetType === 'hypepets_fullscreen' ? (
        // 2026-06-20 — fullscreen HypePets takeover. Same 1920×1080
        // scaler wrap as the hypeegg widget so smaller OBS sources
        // (e.g. 1280×720) scale down without clipping. pointerEvents
        // none on the outer so chat input on the host page still works.
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            overflow: 'hidden',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              width: 1920,
              height: 1080,
              position: 'relative',
              transform: 'scale(min(calc(100vw / 1920), calc(100vh / 1080)))',
              transformOrigin: 'center center',
            }}
          >
            <HypePetsFullscreenWidget
              streamerId={config.streamerId}
              gameStateEvent={gameStateEvent}
              latestChatMessage={chatMessage}
              fields={config.fields}
            />
          </div>
        </div>
      ) : config.widgetType === 'game' ? (
        <UnifiedGameWidget
          streamerId={config.streamerId}
          token={wsToken ?? ''}
          streamerTier={config.streamerTier}
          fields={config.fields}
          liveActiveGame={activeGame}
          gameStateEvent={gameStateEvent}
          chaosSnapshot={chaosSnapshot}
          emojiSnapshot={emojiSnapshot}
          hypeMillionSnapshot={hypeMillionSnapshot}
          hypeDealSnapshot={hypeDealSnapshot}
          drawGuessSnapshot={drawGuessSnapshot}
        />
      ) : config.widgetType === 'chaos_scramble' ? (
        <ChaosScrambleWidget
          streamerId={config.streamerId}
          snapshot={chaosSnapshot}
          fields={config.fields}
        />
      ) : config.widgetType === 'emoji_decoder' ? (
        <EmojiDecoderWidget
          streamerId={config.streamerId}
          snapshot={emojiSnapshot}
          fields={config.fields}
        />
      ) : config.widgetType === 'hype_million' ? (
        <HypeMillionWidget
          streamerId={config.streamerId}
          snapshot={hypeMillionSnapshot}
          fields={config.fields}
        />
      ) : config.widgetType === 'hype_deal' ? (
        <HypeDealWidget
          streamerId={config.streamerId}
          snapshot={hypeDealSnapshot}
          fields={config.fields}
        />
      ) : config.widgetType === 'draw_guess' ? (
        <DrawGuessWidget
          streamerId={config.streamerId}
          snapshot={drawGuessSnapshot}
          fields={config.fields}
        />
      ) : config.widgetType === 'magic_pool_ball' ? (
        <MagicPoolBallWidget
          streamerId={config.streamerId}
          event={magicPoolBallEvent}
          fields={config.fields}
        />
      ) : config.widgetType === 'slots' ? (
        <SlotsWidget streamerId={config.streamerId} event={slotsEvent} fields={config.fields} />
      ) : config.widgetType === 'hypefish' ? (
        // Pass 28an — `?variant=fullscreen` swaps the octagon for the
        // 1920×1080 staged scene (water rises, line casts, item
        // surfaces). Same widget config / token; just a different
        // render path.
        params.get('variant') === 'fullscreen' ? (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              overflow: 'hidden',
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                width: 1920,
                height: 1080,
                position: 'relative',
                transform: 'scale(min(calc(100vw / 1920), calc(100vh / 1080)))',
                transformOrigin: 'center center',
              }}
            >
              <HypefishFullscreenWidget
                gameStateEvent={gameStateEvent}
                streamerTier={config.streamerTier}
              />
            </div>
          </div>
        ) : (
          <HypefishWidget gameStateEvent={gameStateEvent} streamerTier={config.streamerTier} />
        )
      ) : config.widgetType === 'hypeegg' ? (
        // HypePets was designed against a 1920×1080 canvas. Wrap in a
        // fit-to-viewport scaler so smaller browser sources (e.g. 800×800)
        // render the full widget proportionally instead of clipping.
        // `position: fixed` descendants inside this transformed wrapper
        // anchor to it, not the viewport — exactly what we want.
        // Pass 28ad — `?variant=panel` swaps the octagonal popup for
        // the right-edge slide-in panel (recommended at 1920×1080).
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            overflow: 'hidden',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              width: 1920,
              height: 1080,
              position: 'relative',
              transform: 'scale(min(calc(100vw / 1920), calc(100vh / 1080)))',
              transformOrigin: 'center center',
            }}
          >
            {params.get('variant') === 'panel' ? (
              <HypeEggPanelWidget
                gameStateEvent={gameStateEvent}
                streamerTier={config.streamerTier}
              />
            ) : (
              <HypeEggWidget gameStateEvent={gameStateEvent} streamerTier={config.streamerTier} />
            )}
          </div>
        </div>
      ) : config.widgetType === 'jumpscare' ? (
        <JumpscareWidget event={jumpscareEvent} fields={config.fields} />
      ) : config.widgetType === 'hype_meter' ? (
        <HypeMeterWidget snapshot={hypeMeterSnapshot} fields={config.fields} />
      ) : config.widgetType === 'image_rotator' ? (
        <ImageRotatorWidget token={token ?? ''} controlEvent={imageRotatorControl} />
      ) : config.widgetType === 'card-timer' || config.widgetType === 'card_timer' ? (
        <CardTimerWidget fields={config.fields} token={token ?? ''} event={cardTimerEvent} />
      ) : config.widgetType === 'spotify' || config.widgetType === 'spotify_now_playing' ? (
        <SpotifyNowPlayingWidget token={token ?? ''} fields={config.fields} />
      ) : (
        <WidgetView
          widget={widget}
          latestEvent={recentEvent}
          latestChatMessage={chatMessage}
          token={wsToken ?? ''}
          gameStateEvent={gameStateEvent}
          streamerTier={config.streamerTier}
          latestMarathonReveal={marathonReveal}
        />
      )}
    </div>
  );
}
